import {
  useEffect,
  useRef,
  useState,
  createContext,
  useContext,
} from "react";
import {
  useNavigate,
  useLocation,
  Link,
} from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard,
  ReceiptText,
  UtensilsCrossed,
  Soup,
  FolderOpen,
  Puzzle,
  PackageOpen,
  CreditCard,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  Store,
  Loader2,
  Palette,
  Users,
  Volume2,
  VolumeX,
  Bell,
  ClipboardCheck,
  CalendarDays,
  WalletCards,
  Tag,
} from "lucide-react";
import { http } from "@/lib/api";
import { ADMIN_BASE } from "@/lib/config";
import ThemeScope from "@/components/ThemeScope";
import { PRESETS } from "@/lib/themes";

const NAV = [
  ["", LayoutDashboard, "Dashboard", false],
  ["/orders", ReceiptText, "Pesanan", false],
  ["/products", Soup, "Produk", false],
  ["/categories", FolderOpen, "Kategori", false],
  ["/modifiers", Puzzle, "Modifier", false],
  ["/inventory", PackageOpen, "Stok", false],
  ["/tables", UtensilsCrossed, "Meja", false],
  ["/themes", Palette, "Tema", false],
  ["/employees", Users, "Karyawan", true],
  ["/attendance", ClipboardCheck, "Absensi", false, "staff"],
  ["/attendance-admin", CalendarDays, "Absensi Karyawan", true, "owner"],
  ["/payments", CreditCard, "Pembayaran", false],
  ["/vouchers", Tag, "Promo & Voucher", true, "owner"],
  ["/reports", BarChart3, "Laporan", false],
  ["/finance", WalletCards, "Keuangan", true, "owner"],
  ["/settings", Settings, "Pengaturan", false],
];

// =========================================================
// GLOBAL ORDER ALERTS
// =========================================================
// Diletakkan di AdminShell supaya owner dan karyawan tetap
// menerima suara + Web Push di seluruh halaman panel admin.
// =========================================================
const SOUND_STORAGE_KEY = "wm_order_sound_enabled";

const ShellCtx = createContext(null);

export const useAdmin = () => useContext(ShellCtx);

function isIOSDevice() {
  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "";

  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat(
    (4 - (base64String.length % 4)) % 4,
  );

  const base64 = (base64String + padding)
    .replace(/-/g, "+")
    .replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

export default function AdminShell({ children }) {
  const nav = useNavigate();
  const loc = useLocation();

  const [ready, setReady] = useState(false);
  const [user, setUser] = useState(null);

  const [store, setStore] = useState({
    store_name: "Nama Kedai",
    app_name: "Order App",
    admin_panel_name: "Admin Panel",
    logo_url: "",
    admin_theme: {
      tokens: PRESETS.kopi_susu.tokens,
    },
  });

  const [menuOpen, setMenuOpen] = useState(false);

  // =========================================================
  // GLOBAL ORDER SOUND
  // =========================================================

  const [soundEnabled, setSoundEnabled] = useState(
    () => localStorage.getItem(SOUND_STORAGE_KEY) === "1",
  );

  const [soundReady, setSoundReady] = useState(false);

  const [soundProfile, setSoundProfile] = useState({
    sound_key: "default",
    volume: 0.8,
    custom_name: "",
    custom_data: "",
  });

  const audioCtxRef = useRef(null);
  const customBufferRef = useRef(null);
  const customBufferKeyRef = useRef("");
  const beepTimerRef = useRef(null);
  const orderPollRef = useRef(null);
  const previousNewIdsRef = useRef(new Set());
  const initializedOrdersRef = useRef(false);

  // =========================================================
  // GLOBAL PUSH NOTIFICATION
  // =========================================================

  const [notificationEnabled, setNotificationEnabled] =
    useState(false);

  const [notificationLoading, setNotificationLoading] =
    useState(false);

  // =========================================================
  // AUTH + SETTINGS
  // =========================================================

  useEffect(() => {
    if (!localStorage.getItem("wm_token")) {
      nav(`${ADMIN_BASE}/login`);
      return;
    }

    http
      .get("/auth/me")
      .then(({ data }) => {
        setUser(data);
      })
      .catch(() => {
        localStorage.removeItem("wm_token");
        nav(`${ADMIN_BASE}/login`);
      });

    http
      .get("/admin/settings")
      .then(({ data }) => {
        setStore(data);
        setReady(true);
      })
      .catch(() => {
        setReady(true);
      });
  }, []);

  useEffect(() => {
    http
      .get("/admin/my-sound-settings")
      .then(({ data }) => {
        setSoundProfile({
          sound_key: data.sound_key || "default",
          volume: Number(data.volume ?? 0.8),
          custom_name: data.custom_name || "",
          custom_data: data.custom_data || "",
        });
      })
      .catch((e) => {
        console.error("[GLOBAL SOUND] profile load error:", e);
      });
  }, []);

  useEffect(() => {
    customBufferRef.current = null;
    customBufferKeyRef.current = "";
  }, [soundProfile.custom_data]);

  useEffect(() => {
    setMenuOpen(false);
  }, [loc.pathname]);

  // =========================================================
  // AUDIO HELPERS
  // =========================================================

  function getAudioContext() {
    const AudioCtx =
      window.AudioContext || window.webkitAudioContext;

    if (!AudioCtx) {
      return null;
    }

    if (
      !audioCtxRef.current ||
      audioCtxRef.current.state === "closed"
    ) {
      audioCtxRef.current = new AudioCtx();
    }

    return audioCtxRef.current;
  }

  async function unlockAudio() {
    if (!soundEnabled) {
      return false;
    }

    try {
      const ctx = getAudioContext();

      if (!ctx) {
        return false;
      }

      if (ctx.state === "suspended") {
        await ctx.resume();
      }

      const running = ctx.state === "running";
      setSoundReady(running);

      return running;
    } catch (e) {
      console.error("[GLOBAL SOUND] unlock error:", e);
      setSoundReady(false);
      return false;
    }
  }

  function scheduleTone(
    ctx,
    destination,
    freq,
    start,
    duration,
    type = "sine",
    strength = 0.18,
  ) {
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();

    oscillator.type = type;
    oscillator.frequency.setValueAtTime(
      freq,
      ctx.currentTime + start,
    );

    gain.gain.setValueAtTime(
      strength,
      ctx.currentTime + start,
    );

    gain.gain.exponentialRampToValueAtTime(
      0.001,
      ctx.currentTime + start + duration,
    );

    oscillator.connect(gain);
    gain.connect(destination);

    oscillator.start(ctx.currentTime + start);
    oscillator.stop(
      ctx.currentTime + start + duration,
    );
  }

  async function decodeCustomSound(ctx, dataUrl) {
    if (!dataUrl) {
      return null;
    }

    if (
      customBufferRef.current &&
      customBufferKeyRef.current === dataUrl
    ) {
      return customBufferRef.current;
    }

    const comma = dataUrl.indexOf(",");
    if (comma < 0) {
      return null;
    }

    const raw = window.atob(
      dataUrl.slice(comma + 1),
    );

    const bytes = new Uint8Array(raw.length);

    for (let i = 0; i < raw.length; i += 1) {
      bytes[i] = raw.charCodeAt(i);
    }

    const buffer = await ctx.decodeAudioData(
      bytes.buffer.slice(0),
    );

    customBufferRef.current = buffer;
    customBufferKeyRef.current = dataUrl;

    return buffer;
  }

  async function playConfiguredSound(
    profile = soundProfile,
    force = false,
  ) {
    if (!force && !soundEnabled) {
      return;
    }

    try {
      const ctx = audioCtxRef.current;

      if (!ctx || ctx.state !== "running") {
        setSoundReady(false);
        return;
      }

      const volume = Math.max(
        0.05,
        Math.min(1, Number(profile?.volume || 0.8)),
      );

      const master = ctx.createGain();
      master.gain.value = volume;
      master.connect(ctx.destination);

      const key = profile?.sound_key || "default";

      if (key === "custom") {
        const audioBuffer = await decodeCustomSound(
          ctx,
          profile?.custom_data,
        );

        if (!audioBuffer) {
          return;
        }

        const source = ctx.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(master);
        source.start();

        setSoundReady(true);
        return;
      }

      if (key === "kitchen_bell") {
        scheduleTone(
          ctx,
          master,
          1046,
          0,
          0.22,
          "sine",
          0.22,
        );
        scheduleTone(
          ctx,
          master,
          1568,
          0.06,
          0.34,
          "sine",
          0.14,
        );
      } else if (key === "double_bell") {
        scheduleTone(
          ctx,
          master,
          988,
          0,
          0.17,
          "sine",
          0.20,
        );
        scheduleTone(
          ctx,
          master,
          988,
          0.24,
          0.17,
          "sine",
          0.20,
        );
      } else if (key === "soft_chime") {
        scheduleTone(
          ctx,
          master,
          659,
          0,
          0.40,
          "sine",
          0.13,
        );
        scheduleTone(
          ctx,
          master,
          784,
          0.10,
          0.45,
          "sine",
          0.10,
        );
        scheduleTone(
          ctx,
          master,
          988,
          0.20,
          0.50,
          "sine",
          0.08,
        );
      } else if (key === "digital") {
        scheduleTone(
          ctx,
          master,
          880,
          0,
          0.10,
          "square",
          0.10,
        );
        scheduleTone(
          ctx,
          master,
          1175,
          0.13,
          0.10,
          "square",
          0.10,
        );
        scheduleTone(
          ctx,
          master,
          1568,
          0.26,
          0.14,
          "square",
          0.09,
        );
      } else if (key === "pop") {
        scheduleTone(
          ctx,
          master,
          520,
          0,
          0.09,
          "sine",
          0.22,
        );
        scheduleTone(
          ctx,
          master,
          760,
          0.07,
          0.12,
          "sine",
          0.15,
        );
      } else if (key === "classic_bell") {
        scheduleTone(
          ctx,
          master,
          784,
          0,
          0.45,
          "sine",
          0.20,
        );
        scheduleTone(
          ctx,
          master,
          1175,
          0.02,
          0.50,
          "sine",
          0.09,
        );
      } else if (key === "fast_beep") {
        [0, 0.12, 0.24].forEach((t) =>
          scheduleTone(
            ctx,
            master,
            1000,
            t,
            0.07,
            "square",
            0.08,
          ),
        );
      } else if (key === "alert_pulse") {
        scheduleTone(
          ctx,
          master,
          740,
          0,
          0.14,
          "triangle",
          0.16,
        );
        scheduleTone(
          ctx,
          master,
          930,
          0.18,
          0.14,
          "triangle",
          0.16,
        );
        scheduleTone(
          ctx,
          master,
          740,
          0.36,
          0.14,
          "triangle",
          0.16,
        );
      } else if (key === "restaurant_ding") {
        scheduleTone(
          ctx,
          master,
          1319,
          0,
          0.32,
          "sine",
          0.18,
        );
        scheduleTone(
          ctx,
          master,
          1760,
          0.08,
          0.40,
          "sine",
          0.10,
        );
      } else {
        scheduleTone(
          ctx,
          master,
          880,
          0,
          0.18,
          "sine",
          0.18,
        );
        scheduleTone(
          ctx,
          master,
          1100,
          0.18,
          0.24,
          "sine",
          0.16,
        );
      }

      setSoundReady(true);
    } catch (e) {
      console.error(
        "[GLOBAL SOUND] play error:",
        e,
      );
    }
  }

  function playOrderSound() {
    playConfiguredSound(soundProfile);
  }

  async function enableSound() {
    try {
      const AudioCtx =
        window.AudioContext || window.webkitAudioContext;

      if (!AudioCtx) {
        toast.error(
          "Browser ini tidak mendukung suara notifikasi.",
        );
        return;
      }

      localStorage.setItem(SOUND_STORAGE_KEY, "1");
      setSoundEnabled(true);

      const ctx = getAudioContext();

      if (!ctx) {
        throw new Error("AudioContext tidak tersedia.");
      }

      if (ctx.state === "suspended") {
        await ctx.resume();
      }

      if (ctx.state !== "running") {
        setSoundReady(false);

        toast.success(
          "Suara diaktifkan. Tap layar sekali untuk mengaktifkan audio.",
        );

        return;
      }

      setSoundReady(true);

      await playConfiguredSound(
        soundProfile,
        true,
      );

      toast.success("Suara notifikasi aktif");
    } catch (e) {
      console.error("[GLOBAL SOUND] enable error:", e);
      setSoundReady(false);

      toast.error(
        "Suara belum dapat diputar. Tap layar lalu coba lagi.",
      );
    }
  }

  async function disableSound() {
    localStorage.setItem(SOUND_STORAGE_KEY, "0");
    setSoundEnabled(false);
    setSoundReady(false);

    if (beepTimerRef.current) {
      clearInterval(beepTimerRef.current);
      beepTimerRef.current = null;
    }

    try {
      if (
        audioCtxRef.current &&
        audioCtxRef.current.state === "running"
      ) {
        await audioCtxRef.current.suspend();
      }
    } catch (e) {
      console.error("[GLOBAL SOUND] suspend error:", e);
    }

    toast.success("Suara notifikasi dinonaktifkan");
  }

  async function toggleSound() {
    if (soundEnabled) {
      await disableSound();
    } else {
      await enableSound();
    }
  }

  // Restore preference.
  useEffect(() => {
    if (!soundEnabled) {
      setSoundReady(false);
      return;
    }

    try {
      const ctx = getAudioContext();

      if (!ctx) {
        return;
      }

      setSoundReady(ctx.state === "running");
    } catch (e) {
      console.error("[GLOBAL SOUND] init error:", e);
    }
  }, [soundEnabled]);

  // iPhone / Safari: resume on the first user gesture.
  useEffect(() => {
    if (!soundEnabled) {
      return;
    }

    const handleUserGesture = async () => {
      await unlockAudio();
    };

    window.addEventListener(
      "pointerdown",
      handleUserGesture,
    );

    window.addEventListener(
      "touchstart",
      handleUserGesture,
    );

    window.addEventListener(
      "keydown",
      handleUserGesture,
    );

    return () => {
      window.removeEventListener(
        "pointerdown",
        handleUserGesture,
      );

      window.removeEventListener(
        "touchstart",
        handleUserGesture,
      );

      window.removeEventListener(
        "keydown",
        handleUserGesture,
      );
    };
  }, [soundEnabled]);

  // =========================================================
  // GLOBAL ORDER POLLING
  // =========================================================

  useEffect(() => {
    let cancelled = false;

    async function checkOrders() {
      try {
        const { data } = await http.get("/admin/orders");

        if (cancelled) {
          return;
        }

        const newOrders = data.filter(
          (o) => o.status === "new",
        );

        const currentIds = new Set(
          newOrders.map((o) => o.id),
        );

        if (!initializedOrdersRef.current) {
          previousNewIdsRef.current = currentIds;
          initializedOrdersRef.current = true;
        } else {
          const justArrived = newOrders.filter(
            (o) =>
              !previousNewIdsRef.current.has(o.id),
          );

          if (
            justArrived.length > 0 &&
            soundEnabled
          ) {
            playOrderSound();
          }

          previousNewIdsRef.current = currentIds;
        }

        if (beepTimerRef.current) {
          clearInterval(beepTimerRef.current);
          beepTimerRef.current = null;
        }

        if (
          newOrders.length > 0 &&
          soundEnabled
        ) {
          beepTimerRef.current = setInterval(
            () => {
              playOrderSound();
            },
            4000,
          );
        }
      } catch (e) {
        console.error(
          "[GLOBAL ORDERS] polling error:",
          e,
        );
      }
    }

    checkOrders();

    orderPollRef.current = setInterval(
      checkOrders,
      7000,
    );

    return () => {
      cancelled = true;

      if (orderPollRef.current) {
        clearInterval(orderPollRef.current);
        orderPollRef.current = null;
      }

      if (beepTimerRef.current) {
        clearInterval(beepTimerRef.current);
        beepTimerRef.current = null;
      }
    };
  }, [soundEnabled, soundProfile]);

  // =========================================================
  // WEB PUSH
  // =========================================================

  async function enableNotifications() {
    if (notificationLoading) {
      return;
    }

    setNotificationLoading(true);

    try {
      if (!("serviceWorker" in navigator)) {
        toast.error(
          "Browser ini tidak mendukung Service Worker.",
        );
        return;
      }

      if (!("PushManager" in window)) {
        toast.error(
          isIOSDevice()
            ? `Web Push belum tersedia. Tambahkan ${store.app_name || "aplikasi"} ke Home Screen lalu buka dari ikon tersebut.`
            : "Browser ini belum mendukung Web Push. Coba gunakan Chrome, Edge, atau browser terbaru.",
        );
        return;
      }

      if (!("Notification" in window)) {
        toast.error(
          isIOSDevice()
            ? `Notifikasi belum tersedia. Buka ${store.app_name || "aplikasi"} dari ikon Home Screen.`
            : "Browser ini tidak menyediakan Notification API. Gunakan browser terbaru.",
        );
        return;
      }

      let permission = Notification.permission;

      if (permission !== "granted") {
        permission =
          await Notification.requestPermission();
      }

      if (permission !== "granted") {
        if (permission === "denied") {
          toast.error(
            "Notifikasi diblokir. Aktifkan izin notifikasi dari pengaturan perangkat/browser.",
          );
        } else {
          toast.error(
            "Izin notifikasi belum diberikan.",
          );
        }

        return;
      }

      const registration =
        await navigator.serviceWorker.ready;

      const { data } = await http.get(
        "/push/public-key",
      );

      const publicKey = data?.public_key;

      if (!publicKey) {
        throw new Error(
          "VAPID public key tidak tersedia.",
        );
      }

      let subscription =
        await registration.pushManager.getSubscription();

      if (!subscription) {
        subscription =
          await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey:
              urlBase64ToUint8Array(publicKey),
          });
      }

      const subscriptionJson =
        subscription.toJSON();

      if (
        !subscriptionJson.endpoint ||
        !subscriptionJson.keys?.p256dh ||
        !subscriptionJson.keys?.auth
      ) {
        throw new Error(
          "Data Push Subscription tidak lengkap.",
        );
      }

      await http.post("/push/subscribe", {
        endpoint: subscriptionJson.endpoint,
        keys: {
          p256dh:
            subscriptionJson.keys.p256dh,
          auth:
            subscriptionJson.keys.auth,
        },
      });

      setNotificationEnabled(true);
      toast.success("Notifikasi push aktif");
    } catch (e) {
      console.error(
        "[GLOBAL PUSH] subscribe error:",
        e,
      );

      const message =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e?.message ||
        "";

      if (isIOSDevice()) {
        toast.error(
          message
            ? `Gagal mengaktifkan Web Push: ${message}`
            : `Gagal mengaktifkan Web Push. Jika memakai iPhone/iPad, pastikan ${store.app_name || "aplikasi"} dibuka dari ikon Home Screen.`,
        );
      } else {
        toast.error(
          message
            ? `Gagal mengaktifkan Web Push: ${message}`
            : "Gagal mengaktifkan Web Push. Periksa izin notifikasi browser lalu coba lagi.",
        );
      }
    } finally {
      setNotificationLoading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;

    async function checkPushSubscription() {
      try {
        if (
          !("serviceWorker" in navigator) ||
          !("PushManager" in window) ||
          !("Notification" in window)
        ) {
          if (!cancelled) {
            setNotificationEnabled(false);
          }
          return;
        }

        if (
          Notification.permission !== "granted"
        ) {
          if (!cancelled) {
            setNotificationEnabled(false);
          }
          return;
        }

        const registration =
          await navigator.serviceWorker.ready;

        const subscription =
          await registration.pushManager.getSubscription();

        if (!cancelled) {
          setNotificationEnabled(
            Boolean(subscription),
          );
        }
      } catch (e) {
        console.error(
          "[GLOBAL PUSH] status check error:",
          e,
        );

        if (!cancelled) {
          setNotificationEnabled(false);
        }
      }
    }

    checkPushSubscription();

    return () => {
      cancelled = true;
    };
  }, []);

  // =========================================================
  // SHELL
  // =========================================================

  const tokens =
    user?.admin_theme?.tokens ||
    store.admin_theme?.tokens ||
    PRESETS.kopi_susu.tokens;

  const isOwner = user?.role === "owner";

  const items = NAV.filter((n) => {
    const ownerOnly = Boolean(n[3]);
    const audience = n[4] || "all";

    if (ownerOnly && !isOwner) return false;
    if (audience === "staff" && isOwner) return false;
    if (audience === "owner" && !isOwner) return false;

    return true;
  });

  const isActive = (h) =>
    h === ADMIN_BASE
      ? loc.pathname === ADMIN_BASE
      : loc.pathname.startsWith(h);

  function logout() {
    localStorage.removeItem("wm_token");
    nav(`${ADMIN_BASE}/login`);
  }

  if (!ready) {
    return (
      <ThemeScope
        tokens={tokens}
        className="flex min-h-dvh items-center justify-center"
      >
        <Loader2 className="wm-spin text-muted-foreground" />
      </ThemeScope>
    );
  }

  return (
    <ShellCtx.Provider
      value={{
        store,
        setStore,
        tokens,
        user,
        setUser,
        soundEnabled,
        soundReady,
        soundProfile,
        setSoundProfile,
        notificationEnabled,
      }}
    >
      <ThemeScope
        tokens={tokens}
        className="min-h-dvh"
      >
        <div className="mx-auto flex min-h-dvh w-full max-w-6xl">
          <aside className="sticky top-0 hidden h-dvh w-60 flex-col border-r border-border bg-card p-4 md:flex">
            <Brand store={store} />

            <nav className="mt-6 flex-1 space-y-1 overflow-y-auto no-scrollbar">
              {items.map(
                ([suf, Icon, label]) => {
                  const h = ADMIN_BASE + suf;

                  return (
                    <Link
                      key={h}
                      to={h}
                      data-testid={`nav-${label.toLowerCase()}`}
                      className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${
                        isActive(h)
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:bg-muted"
                      }`}
                    >
                      <Icon size={18} />
                      {label}
                    </Link>
                  );
                },
              )}
            </nav>

            {user && (
              <div className="mb-2 rounded-xl bg-muted px-3 py-2 text-xs">
                <div className="font-bold text-card-foreground">
                  {user.name}
                </div>

                <div className="text-muted-foreground">
                  {user.role === "owner"
                    ? "Pemilik"
                    : "Karyawan"}
                </div>
              </div>
            )}

            <button
              onClick={logout}
              data-testid="logout-btn"
              className="flex items-center gap-3 rounded-xl border border-border px-3 py-2.5 text-sm font-semibold text-card-foreground hover:bg-muted"
            >
              <LogOut size={18} />
              Keluar
            </button>
          </aside>

          <div className="flex min-w-0 flex-1 flex-col">
            <header className="sticky top-0 z-30 flex items-center justify-between border-b border-border bg-background/85 px-4 py-3 backdrop-blur-xl md:hidden">
              <Brand
                store={store}
                compact
              />

              <button
                onClick={() =>
                  setMenuOpen(true)
                }
                className="grid h-10 w-10 place-items-center rounded-xl bg-muted"
              >
                <Menu size={20} />
              </button>
            </header>

            <main className="min-w-0 flex-1 p-4 md:p-8">
              <GlobalAlertControls
                soundEnabled={soundEnabled}
                soundReady={soundReady}
                toggleSound={toggleSound}
                notificationEnabled={
                  notificationEnabled
                }
                notificationLoading={
                  notificationLoading
                }
                enableNotifications={
                  enableNotifications
                }
              />

              {children}
            </main>
          </div>

          {menuOpen && (
            <div className="fixed inset-0 z-40 md:hidden">
              <div
                className="absolute inset-0 bg-black/60"
                onClick={() =>
                  setMenuOpen(false)
                }
              />

              <aside className="absolute left-0 top-0 h-full w-72 overflow-y-auto border-r border-border bg-card p-4">
                <div className="flex items-center justify-between">
                  <Brand store={store} />

                  <button
                    onClick={() =>
                      setMenuOpen(false)
                    }
                    className="grid h-9 w-9 place-items-center rounded-xl bg-muted"
                  >
                    <X size={18} />
                  </button>
                </div>

                <nav className="mt-6 space-y-1">
                  {items.map(
                    ([suf, Icon, label]) => {
                      const h =
                        ADMIN_BASE + suf;

                      return (
                        <Link
                          key={h}
                          to={h}
                          className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold ${
                            isActive(h)
                              ? "bg-primary text-primary-foreground"
                              : "text-muted-foreground"
                          }`}
                        >
                          <Icon size={18} />
                          {label}
                        </Link>
                      );
                    },
                  )}
                </nav>

                <button
                  onClick={logout}
                  className="mt-4 flex w-full items-center gap-3 rounded-xl border border-border px-3 py-2.5 text-sm font-semibold"
                >
                  <LogOut size={18} />
                  Keluar
                </button>
              </aside>
            </div>
          )}
        </div>
      </ThemeScope>
    </ShellCtx.Provider>
  );
}

function GlobalAlertControls({
  soundEnabled,
  soundReady,
  toggleSound,
  notificationEnabled,
  notificationLoading,
  enableNotifications,
}) {
  return (
    <div className="mb-5 flex flex-wrap items-center gap-2 rounded-2xl border border-border bg-card/70 p-2.5">
      <button
        type="button"
        onClick={toggleSound}
        className={`flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-bold transition ${
          soundEnabled
            ? "bg-emerald-500/15 text-emerald-500"
            : "bg-primary text-primary-foreground"
        }`}
      >
        {soundEnabled ? (
          <Volume2 size={15} />
        ) : (
          <VolumeX size={15} />
        )}

        {soundEnabled
          ? "Suara Aktif ✓"
          : "Aktifkan Suara"}
      </button>

      <button
        type="button"
        disabled={notificationLoading}
        onClick={
          notificationEnabled
            ? undefined
            : enableNotifications
        }
        className={`flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-bold transition ${
          notificationEnabled
            ? "cursor-default bg-emerald-500/15 text-emerald-500"
            : "bg-muted text-muted-foreground hover:bg-muted/80"
        } ${
          notificationLoading
            ? "cursor-wait opacity-60"
            : ""
        }`}
      >
        <Bell size={15} />

        {notificationLoading
          ? "Mengaktifkan..."
          : notificationEnabled
          ? "Notifikasi Aktif ✓"
          : "Aktifkan Notifikasi"}
      </button>

      {soundEnabled && !soundReady && (
        <span className="text-[11px] text-muted-foreground">
          Tap layar sekali jika suara belum terdengar.
        </span>
      )}
    </div>
  );
}

function Brand({ store, compact }) {
  return (
    <Link
      to={ADMIN_BASE}
      className="flex items-center gap-2.5"
    >
      <div
        className={`grid h-10 w-10 flex-none place-items-center overflow-hidden bg-primary text-primary-foreground ${
          store.logo_shape === "circle"
            ? "rounded-full"
            : "rounded-xl"
        }`}
      >
        {store.logo_url ? (
          <img
            src={store.logo_url}
            alt="logo"
            className="h-full w-full object-cover"
          />
        ) : (
          <Store size={18} />
        )}
      </div>

      <div className="min-w-0">
        <div className="truncate font-heading text-sm font-extrabold text-card-foreground">
          {store.store_name}
        </div>

        {!compact && (
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            {store.admin_panel_name || "Admin Panel"}
          </div>
        )}
      </div>
    </Link>
  );
}

export function PageHead({
  eyebrow,
  title,
  desc,
  children,
}) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        {eyebrow && (
          <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-primary">
            {eyebrow}
          </span>
        )}

        <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight text-foreground">
          {title}
        </h1>

        {desc && (
          <p className="mt-1 text-sm text-muted-foreground">
            {desc}
          </p>
        )}
      </div>

      {children}
    </div>
  );
}